import { CommonModule } from '@angular/common';
import { AfterViewInit, Component } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import Chart from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import {
  AssetValues,
  Client,
  CommandValues,
  CommunicationActivityDataOut,
  DashboardCommunicationActivityDataIn,
  DashboardQueryDataIn,
  EventValues,
  GuidDictionaryItem,
  PermissionsEnum,
} from '../../../api/api-reference';
import { ToastrService } from 'ngx-toastr';
import { SplitCamelCasePipe } from '../../../pipes/split-camel-case';
import { AuthService } from '../../../services/auth.service';
import { TableScrollService } from '../../../services/table-scroll.service';
import { DateFilterComponent } from '../../core/components/date-filter/date-filter.component';
import { getAlertColor } from '../../events-alarms/alert-colors';
import { DropdownFilterComponent } from '../../core/components/dropdown-filter/dropdown-filter.component';

Chart.register(zoomPlugin);

@Component({
  selector: 'app-analytics',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    RouterLink,
    SplitCamelCasePipe,
    TranslateModule,
    DateFilterComponent,
    DropdownFilterComponent
  ],
  templateUrl: './analytics.component.html',
  styleUrl: './analytics.component.scss',
})
export class AnalyticsComponent implements AfterViewInit {
  //asset
  chart: any = [];
  labels: string[] = [];
  data: number[] = [];
  backgroundColors: string[] = [];
  backgroundColorsRemote: string[] = [];
  allAssets: number = 0;
  selectedRegion: string | undefined = undefined;
  selectedArea: string | undefined = undefined;
  regions: GuidDictionaryItem[] = [];
  areas: GuidDictionaryItem[] = [];
  assetTypes: GuidDictionaryItem[] = [];
  selectedAssetType: string | undefined = undefined;

  analyticsData: AssetValues[] = [];
  analyticsRemote: CommandValues[] = [];
  analyticsAlarms: EventValues[] = [];
  analyticsEvents: EventValues[] = [];

  //remmote commands
  chartRemote: any = [];
  labelsRemote: string[] = [];
  dataRemote: number[] = [];
  totalRemoteCommands: number = 0;

  //colors
  predefinedColors: string[] = [
    'rgba(203, 195, 8, 1)',
    'rgba(235, 160, 48, 1)',
    'rgba(229, 114, 89, 1)',
    'rgba(144, 75, 231, 1)',
    'rgba(38, 196, 120, 1)',
    'rgba(32, 143, 223, 1)',
    'rgb(8 59 203)',
    'rgb(164 8 203)',
    'rgb(203 8 143)',
    'rgb(203 80 8)',
    'rgb(173 203 8)',
    'rgb(203 8 8)',
    'rgb(203 98 8)',
    'rgb(8, 59, 203)',
    'rgb(146 203 8)',
  ];
  colorClasses = ['red', 'gray', 'green', 'light-green', 'yellow', 'purple'];

  //permissions
  PermissionEnum = PermissionsEnum;

  //dateFilter
  public selectedOption: string | undefined = 'Last 24h';
  isDateFilterOpen = false;
  milisecondsInADay = 24 * 60 * 60 * 1000;
  now = new Date();

  startDateCommand = new Date(this.now.getTime() - this.milisecondsInADay);
  startDateEvent = new Date(this.now.getTime() - this.milisecondsInADay);

  constructor(
    private client: Client,
    private toastr: ToastrService,
    public authService: AuthService,
    private tableScrollService: TableScrollService,
    private router: Router,
  ) { }

  navigateToEvents(channelId: string) {
    this.router.navigate(['/events/events'], { state: { channelIds: [channelId] } });
  }

  navigateToAlarms(channelId: string) {
    this.router.navigate(['/events/alarms'], { state: { channelIds: [channelId] } });
  }

  ngAfterViewInit(): void {
    this.getRegions();
    this.getAssetTypes();
    this.getAssets();
    this.getRemoteCommands();
    this.getEvents();
    this.getAlarms();
    this.getCommunicationActivity();
  }

  get orderedRemoteCommands(): CommandValues[] {
    return this.analyticsRemote.sort((a, b) => (b.success ?? 0) + (b.pending ?? 0) + (b.failed ?? 0) - ((a.success ?? 0) + (a.pending ?? 0) + (a.failed ?? 0)));
  }

  getGradientStyle(name: string): { [key: string]: string } {
    const color = getAlertColor(name);
    return {
      background: `linear-gradient(to left, var(--analytics-container) 0%, color-mix(in srgb, ${color} 40%, transparent))`
    };
  }

  getRemoteCommands() {
    const dataOut = new DashboardQueryDataIn({
      startDate: this.startDateCommand,
      area: this.selectedArea,
      region: this.selectedRegion
    });

    this.client.getRemoteCommands(dataOut).subscribe({
      next: (response) => {
        this.analyticsRemote = response;
        this.totalRemoteCommands =
          this.analyticsRemote.reduce(
            (sum, item) => sum + item.success! + item.failed! + item.pending!,
            0,
          ) ?? 0;
        this.setUpCommandChart();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getAlarms() {
    const dataOut = new DashboardQueryDataIn({
      startDate: this.startDateEvent,
      area: this.selectedArea,
      region: this.selectedRegion
    });

    this.client.getAlarms(dataOut).subscribe({
      next: (response) => {
        this.analyticsAlarms = response;
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getEvents() {
    const dataOut = new DashboardQueryDataIn({
      startDate: this.startDateEvent,
      area: this.selectedArea,
      region: this.selectedRegion
    });

    this.client.getEvents(dataOut).subscribe({
      next: (response) => {
        this.analyticsEvents = response;
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getAssets() {
    this.client.getAssets().subscribe({
      next: (response) => {
        this.analyticsData = response;
        this.allAssets =
          this.analyticsData.reduce(
            (sum, item) => sum + (item.on ?? 0) + (item.off ?? 0),
            0,
          ) ?? 0;
        this.setUpAssetChart();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getAssetTypes() {
    this.client.getAllAssetTypesForOptions(undefined).subscribe({
      next: (response) => {
        this.assetTypes = response;
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  onAssetTypeChange(value?: string): void {
    this.selectedAssetType = value === '' ? undefined : value;
    this.getCommunicationActivity();
  }

  getRegions() {
    this.client.getAllRegionsForOptions().subscribe({
      next: (response) => {
        this.regions = response;
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getAreas() {
    if (!this.selectedRegion) {
      this.areas = [];
      return;
    }

    this.client.getAllAreaByRegionForOptions(this.selectedRegion).subscribe({
      next: (response) => {
        this.areas = response;
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  setUpAssetChart() {
    this.labels = this.analyticsData.map((item) => item.assetTypeName!);
    this.data = this.analyticsData.map(
      (item) => (item.on ?? 0) + (item.off ?? 0),
    );

    this.backgroundColors = this.predefinedColors.slice(
      0,
      this.analyticsData.length,
    );

    this.chart = new Chart('pie', {
      type: 'pie',
      data: {
        labels: this.labels,
        datasets: [
          {
            label: '# of Votes',
            data: this.data,
            borderWidth: 0,
            backgroundColor: this.backgroundColors,
          },
        ],
      },
      options: {
        aspectRatio: 1,
        plugins: {
          legend: {
            display: false,
          },
        },
      },
    });
  }

  setUpCommandChart() {
    this.labelsRemote = this.analyticsRemote.map((item) => item.commandType!);
    this.dataRemote = this.analyticsRemote.map(
      (item) => item.failed! + item.pending! + item.success!,
    );

    this.backgroundColorsRemote = this.predefinedColors.slice(
      0,
      this.analyticsRemote.length,
    );

    if (!this.chartRemote.data) {
      this.chartRemote = new Chart('doughnut', {
        type: 'doughnut',
        data: {
          labels: this.labelsRemote,
          datasets: [
            {
              label: '# of Votes',
              data: this.dataRemote,
              borderWidth: 0,
              backgroundColor: this.backgroundColorsRemote,
            },
          ],
        },
        options: {
          aspectRatio: 1,
          plugins: {
            legend: {
              display: false,
            },
          },
          cutout: '70%',
        },
      });
    } else {
      this.chartRemote.data.labels = this.labelsRemote;
      this.chartRemote.data.datasets[0].data = this.dataRemote;
      this.chartRemote.data.datasets[0].backgroundColor =
        this.backgroundColorsRemote;
      this.chartRemote.update();
    }
  }

  getSum(item: CommandValues) {
    return (item.failed ?? 0) + (item.pending ?? 0) + (item.success ?? 0);
  }

  onDateFilterEvent(selectedFilter?: string) {
    if (selectedFilter) {
      this.startDateEvent = this.applyDateRange(selectedFilter);
      this.getEvents();
      this.getAlarms();
    }
  }

  onDateFilterCommand(selectedFilter?: string) {
    if (selectedFilter) {
      this.startDateCommand = this.applyDateRange(selectedFilter);
      this.getRemoteCommands();
    }
  }

  private applyDateRange(filter: string): Date {
    switch (filter) {
      case 'LAST24h':
        return new Date(this.now.getTime() - this.milisecondsInADay);
      case 'LAST7DAYS':
        return new Date(this.now.getTime() - this.milisecondsInADay * 7);
      case 'LAST30DAYS':
        return new Date(this.now.getTime() - this.milisecondsInADay * 30);
      default:
        return new Date(this.now.getTime() - this.milisecondsInADay);
    }
  }

  onRegionChange(value?: string): void {
    this.selectedRegion = value;
    this.selectedArea = undefined;

    if (value == "")
      this.selectedRegion = undefined;

    this.getEvents();
    this.getAlarms();
    this.getRemoteCommands();
    this.getCommunicationActivity();
    this.getAreas();
  }

  onAreaChange(value?: string): void {
    this.selectedArea = value;
    if (value == "")
      this.selectedArea = undefined;
    this.getEvents();
    this.getAlarms();
    this.getRemoteCommands();
    this.getCommunicationActivity();
  }

  // Online/Offline activity chart
  communicationActivity: CommunicationActivityDataOut[] = [];
  chartTimes: Date[] = [];
  selectedTimeRange = '1D';
  onlineOfflineChart: any;
  timeRanges = ['1D', '7D', '30D'];
  aggregated = true;

  private rangeMs: Record<string, number> = {
    '1D': this.milisecondsInADay,
    '7D': 7 * this.milisecondsInADay,
    '30D': 30 * this.milisecondsInADay,
  };

  // bucket size (minutes) the backend averages the raw 15-min data into when the switch is on
  private aggregationMinutesByRange: Record<string, number> = {
    '1D': 30,
    '7D': 120,
    '30D': 360,
  };

  getCommunicationActivity(): void {
    const dataOut = new DashboardCommunicationActivityDataIn({
      startDate: new Date(this.now.getTime() - this.rangeMs[this.selectedTimeRange]),
      area: this.selectedArea,
      region: this.selectedRegion,
      assetType: this.selectedAssetType,
      intervalMinutes: this.aggregated ? this.aggregationMinutesByRange[this.selectedTimeRange] : undefined,
    });

    this.client.getCommunicationActivity(dataOut).subscribe({
      next: (response) => {
        this.communicationActivity = response;
        this.setUpOnlineOfflineChart();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  private getFilteredData(): { labels: string[]; online: number[]; offline: number[] } {
    const entries = [...this.communicationActivity]
      .sort((a, b) => new Date(a.bucketTime!).getTime() - new Date(b.bucketTime!).getTime());

    this.chartTimes = entries.map((v) => new Date(v.bucketTime!));

    return {
      labels: entries.map((v) => this.formatTimeLabel(new Date(v.bucketTime!))),
      online: entries.map((v) => v.online ?? 0),
      offline: entries.map((v) => v.offline ?? 0),
    };
  }

  private formatTimeLabel(date: Date): string {
    if (this.selectedTimeRange === '7D' || this.selectedTimeRange === '30D') {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    const h = date.getHours().toString().padStart(2, '0');
    const m = date.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  private xTicksByRange: Record<string, number> = { '1D': 24, '7D': 7, '30D': 30 };
  private daysPerTickByRange: Record<string, number> = { '7D': 1, '30D': 2 };

  private dayStartIndices(times: Date[]): Set<number> {
    const indices = new Set<number>();
    let prevKey = '';
    times.forEach((d, i) => {
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (key !== prevKey) {
        indices.add(i);
        prevKey = key;
      }
    });
    return indices;
  }

  private verticalFill(rgb: string, topAlpha: number, bottomAlpha: number) {
    return (ctx: any) => {
      const chartArea = ctx.chart.chartArea;
      if (!chartArea) return `rgba(${rgb}, ${bottomAlpha})`;
      const gradient = ctx.chart.ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      gradient.addColorStop(0, `rgba(${rgb}, ${topAlpha})`);
      gradient.addColorStop(1, `rgba(${rgb}, ${bottomAlpha})`);
      return gradient;
    };
  }

  setUpOnlineOfflineChart(): void {
    const { labels, online, offline } = this.getFilteredData();
    const maxTicks = this.xTicksByRange[this.selectedTimeRange] ?? 8;

    if (this.onlineOfflineChart?.destroy) {
      this.onlineOfflineChart.destroy();
    }

    this.onlineOfflineChart = new Chart('onlineOfflineChart', {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Online',
            data: online,
            borderColor: 'rgba(0, 165, 85, 0.85)',
            backgroundColor: this.verticalFill('0, 165, 85', 0.35, 0.08),
            fill: true,
            tension: 0.6,
            pointRadius: 0,
            borderWidth: 2,
          },
          {
            label: 'Offline',
            data: offline,
            borderColor: 'rgba(215, 38, 61, 0.85)',
            backgroundColor: this.verticalFill('215, 38, 61', 0.35, 0.08),
            fill: true,
            tension: 0.6,
            pointRadius: 0,
            borderWidth: 2.2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          // click-drag-release over a time span to zoom into it. mode is 'x' so the drag
          // selection always spans the chart's full height; resetZoom() clears it.
          zoom: {
            zoom: {
              drag: {
                enabled: true,
                backgroundColor: 'rgba(38, 196, 120, 0.15)',
                borderColor: 'rgba(38, 196, 120, 0.4)',
                borderWidth: 1,
              },
              mode: 'x',
            },
          },
          legend: {
            display: true,
            position: 'top',
            labels: {
              color: 'rgba(200,200,200,0.9)',
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 6,
              boxHeight: 6,
              font: { size: 12 },
            },
          },
          tooltip: {
            usePointStyle: true,
            callbacks: {
              title: (items) => {
                const label = items[0]?.label ?? '';
                const d = this.chartTimes[items[0]?.dataIndex];
                if (!d) return label;
                return d.toLocaleString('en-GB', {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                });
              },
              label: (ctx) => {
                const val = ctx.parsed.y;
                const formatted = val >= 1000 ? `${(val / 1000).toFixed(2)}k` : String(val);
                return ` ${ctx.dataset.label} ${formatted}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: 'rgba(255, 255, 255, 0)' },
            afterBuildTicks: (axis) => {
              if (this.selectedTimeRange === '1D') return;
              const step = this.daysPerTickByRange[this.selectedTimeRange] ?? 1;
              const days = [...this.dayStartIndices(this.chartTimes)];
              const last = days.length - 1;
              // count back from the newest day so today's tick is always kept
              const keep = new Set(days.filter((_, i) => (last - i) % step === 0));
              axis.ticks = axis.ticks.filter((t) => keep.has(t.value));
            },
            ticks: { color: 'rgba(180,180,180,0.9)', autoSkip: this.selectedTimeRange === '1D', maxTicksLimit: maxTicks, font: { size: 11 }, maxRotation: 0, minRotation: 0 },
          },
          y: {
            grid: { color: 'rgba(255,255,255,0.09)' },
            ticks: {
              color: 'rgba(180,180,180,0.9)',
              font: { size: 11 },
              callback: (val) => {
                const n = Number(val);
                return n >= 1000 ? `${n / 1000}k` : n;
              },
            },
          },
        },
      },
    });
  }

  onTimeRangeChange(range: string): void {
    this.selectedTimeRange = range;
    this.getCommunicationActivity();
  }

  onAggregationToggle(): void {
    this.aggregated = !this.aggregated;
    this.getCommunicationActivity();
  }

  resetZoom(): void {
    this.onlineOfflineChart?.resetZoom();
  }
}
